import { CustomerAccountForm } from './customer-account-form';
import { roleLabel, actionLabel, bundleLabel, errorLabel } from './display-copy';
import { useEffect, useState } from 'react';
import {
  authorityInventory,
  assignStaff,
  inviteCustomer,
  acceptInvitation,
  revokeAuthority,
  projectNotifications,
  searchProject,
  type AuthorityInventory,
} from '../../lib/oem-client';
import { runtimeMode, type OemContext } from '../../lib/auth';
const staffRoles = [
  'coordinator',
  'rd',
  'rd_checker',
  'qa',
  'owner',
  'finance',
  'purchasing',
  'designer',
  'admin',
  'auditor',
  'production',
] as const;
const customerRoles = [
  'customer-requester',
  'customer-sensory',
  'customer-designer',
  'customer-commercial',
  'customer-finance',
] as const;
export function InvitationAcceptance() {
  const [token, setToken] = useState(''),
    [message, setMessage] = useState(''),
    [busy, setBusy] = useState(false);
  async function accept() {
    setBusy(true);
    try {
      await acceptInvitation(token.trim());
      setMessage('รับคำเชิญแล้ว · โหลดพื้นที่ทำงานอีกครั้ง');
      setToken('');
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'invitation_unavailable');
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="card">
      <h2>รับคำเชิญสำหรับบัญชีนี้</h2>
      <p>คำเชิญผูกกับผู้รับและขอบเขต ใช้ได้ครั้งเดียวก่อนหมดอายุ</p>
      <label>
        รหัสคำเชิญ
        <input
          type="password"
          autoComplete="off"
          value={token}
          onChange={(e) => setToken(e.target.value)}
        />
      </label>
      <button
        className="action"
        disabled={
          runtimeMode !== 'connected' ||
          busy ||
          !/^[a-f0-9]{64}$/.test(token.trim()) ||
          !navigator.onLine
        }
        onClick={() => void accept()}
      >
        รับคำเชิญ
      </button>
      {message ? <p role="status">{message}</p> : null}
      <button className="action secondary" onClick={() => location.reload()}>
        โหลดพื้นที่ทำงานอีกครั้ง
      </button>
    </section>
  );
}
export function AuthorityAdministration({ context }: { readonly context: OemContext }) {
  const [data, setData] = useState<AuthorityInventory | null>(null),
    [role, setRole] = useState<string>('customer-requester'),
    [user, setUser] = useState(''),
    [evidence, setEvidence] = useState(''),
    [expires, setExpires] = useState(() =>
      new Date(Date.now() + 86400000).toISOString().slice(0, 16),
    ),
    [message, setMessage] = useState(''),
    [invitation, setInvitation] = useState<string | null>(null),
    [busy, setBusy] = useState(false);
  const staff = staffRoles.includes(role as (typeof staffRoles)[number]);
  async function load() {
    if (runtimeMode !== 'connected') return;
    try {
      setData(await authorityInventory(context.projectId));
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'scope_denied');
    }
  }
  useEffect(() => {
    if (runtimeMode === 'connected')
      void authorityInventory(context.projectId)
        .then(setData)
        .catch((e: unknown) => setMessage(e instanceof Error ? e.message : 'scope_denied'));
  }, [context.projectId]);
  async function submit() {
    setBusy(true);
    setMessage('');
    setInvitation(null);
    try {
      const body = {
        projectId: context.projectId,
        userId: user,
        role,
        evidence,
        expiresAt: new Date(expires).toISOString(),
      };
      const result = staff ? await assignStaff(body) : await inviteCustomer(body);
      if (!staff) setInvitation((result as { token: string }).token);
      setMessage(
        staff ? 'บันทึกการมอบหมายแล้ว' : 'สร้างคำเชิญแล้ว · ยังไม่มีสิทธิ์จนกว่าบัญชีผู้รับจะรับ',
      );
      await load();
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'scope_denied');
    } finally {
      setBusy(false);
    }
  }
  async function revoke(id: string) {
    setBusy(true);
    try {
      await revokeAuthority(context.projectId, id);
      setMessage('เพิกถอนแล้ว การอ่านและตัดสินใจครั้งต่อไปตรวจสิทธิ์ใหม่');
      await load();
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'scope_denied');
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <CustomerAccountForm key={context.projectId} context={context} />
      <section className="card">
        <div className="eyebrow">NAMED RESPONSIBILITY</div>
        <h2>มอบหมายหน้าที่ตามขอบเขต</h2>
        <p>
          ใช้ template ที่กำหนดบนเซิร์ฟเวอร์ บันทึกเหตุผลและวันหมดอายุ
          ผู้ดูแลมอบบทบาทสูงขึ้นให้ตนเองไม่ได้
        </p>
        {runtimeMode === 'fixture' ? (
          <p className="note-warning">
            Fixture แสดงการมอบหมายเท่านั้น การเปลี่ยนสิทธิ์ต้องใช้ connected mode
          </p>
        ) : null}
        <label>
          บทบาท
          <select
            value={role}
            onChange={(e) => {
              setRole(e.target.value);
              setUser('');
            }}
          >
            {[...customerRoles, ...staffRoles].map((r) => (
              <option key={r}>{r}</option>
            ))}
          </select>
        </label>
        <label>
          {staff ? 'สมาชิกองค์กร' : 'บัญชีผู้รับ UUID'}
          {staff ? (
            <select value={user} onChange={(e) => setUser(e.target.value)}>
              <option value="">เลือกสมาชิกที่มีอยู่</option>
              {data?.members.map((m) => (
                <option key={m.userId} value={m.userId}>
                  {m.userId} · {roleLabel(m.role)}
                </option>
              ))}
            </select>
          ) : (
            <input
              value={user}
              onChange={(e) => setUser(e.target.value)}
              placeholder="UUID ของบัญชีที่ยืนยันแล้ว"
            />
          )}
        </label>
        <label>
          หลักฐานการมอบหมาย
          <textarea value={evidence} onChange={(e) => setEvidence(e.target.value)} />
        </label>
        <label>
          หมดอายุ (UTC)
          <input
            type="datetime-local"
            value={expires}
            onChange={(e) => setExpires(e.target.value)}
          />
        </label>
        <button
          className="action"
          disabled={!data || busy || !user || !evidence || !navigator.onLine}
          onClick={() => void submit()}
        >
          {staff ? 'บันทึกการมอบหมาย' : 'สร้างคำเชิญ'}
        </button>
        {invitation ? (
          <label>
            รหัสครั้งเดียว · เก็บให้ผู้รับที่ระบุเท่านั้น
            <input readOnly type="password" value={invitation} />
            <button className="action secondary" onClick={() => setInvitation(null)}>
              ปิดรหัส
            </button>
          </label>
        ) : null}
        {message ? <p role="status">{message}</p> : null}
      </section>
      {data ? (
        <>
          <section className="card">
            <h2>สิทธิ์ที่ระบุชื่อ</h2>
            {data.authorities.map((a) => (
              <article key={a.id} className="scope">
                <strong>
                  {roleLabel(a.role)} · v{a.version}
                </strong>
                <p>{a.userId}</p>
                <p>
                  {a.readBundles.map(bundleLabel).join(' · ')} ·{' '}
                  {a.actions.map(actionLabel).join(' · ')}
                </p>
                <details>
                  <summary>รหัสสิทธิ์สำหรับผู้ดูแล</summary>
                  <p className="wrap">{a.actions.join(' · ')}</p>
                </details>
                <p>
                  {a.evidence} · {a.validUntil}
                </p>
                <button
                  className="action danger"
                  disabled={busy || Boolean(a.revokedAt) || !navigator.onLine}
                  onClick={() => void revoke(a.id)}
                >
                  {a.revokedAt ? 'เพิกถอนแล้ว' : 'เพิกถอนสิทธิ์นี้'}
                </button>
              </article>
            ))}
          </section>
          <section className="card">
            <h2>คำเชิญและประวัติ</h2>
            {data.invitations.map((i) => (
              <p key={i.id}>
                {roleLabel(i.role)} · {i.userId} ·{' '}
                {i.acceptedAt ? 'รับแล้ว' : i.revokedAt ? 'ยกเลิกแล้ว' : 'รอรับ'} · {i.expiresAt}
              </p>
            ))}
            {data.events.map((e) => (
              <p key={e.id}>
                {e.event} · {e.authorityId} · {e.createdAt}
              </p>
            ))}
          </section>
        </>
      ) : null}
      <InvitationAcceptance />
    </>
  );
}
export function ProjectCoordination({ context }: { readonly context: OemContext }) {
  const [q, setQ] = useState(''),
    [results, setResults] = useState<
      { id: string; kind: string; label: string; version: number }[]
    >([]),
    [notifications, setNotifications] = useState<
      { id: string; label: string; receiptId: string; createdAt: string }[]
    >([]),
    [error, setError] = useState('');
  useEffect(() => {
    if (runtimeMode === 'connected')
      void projectNotifications(context.projectId)
        .then(setNotifications)
        .catch((e: unknown) => setError(e instanceof Error ? e.message : 'unavailable'));
  }, [context.projectId]);
  async function search() {
    try {
      const data = (await searchProject(context.projectId, q)) as {
        id: string;
        kind: string;
        label: string;
        version: number;
      }[];
      setResults(data);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'unavailable');
    }
  }
  return (
    <section className="card">
      <h2>ค้นหาในขอบเขตและการแจ้งเตือน</h2>
      <p>ผลค้นหาแสดงเฉพาะข้อมูลตามสิทธิ์ การแจ้งเตือนอ้างอิงรายการที่บันทึกสำเร็จ</p>
      <label>
        ค้นหา
        <input value={q} onChange={(e) => setQ(e.target.value)} />
      </label>
      <button
        className="action secondary"
        disabled={runtimeMode !== 'connected' || !navigator.onLine}
        onClick={() => void search()}
      >
        ค้นหา
      </button>
      {results.map((r) => (
        <p key={r.id}>
          {r.kind} · {r.label} v{r.version} · {r.id}
        </p>
      ))}
      <h3>รายการล่าสุด</h3>
      {notifications.map((n) => (
        <p key={n.id}>
          {n.label} · receipt {n.receiptId} · {n.createdAt}
        </p>
      ))}
      {notifications.length === 0 ? <p>ไม่มีรายการในขอบเขตที่อ่านได้</p> : null}
      {error ? (
        <p className="note-danger" role="alert">
          {errorLabel(error)}
        </p>
      ) : null}
    </section>
  );
}
